import TextButton from 'LuxKit/Button/TextButton'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import BodyText from 'LuxKit/Typography/BodyText'
import ModalContext from 'contexts/ModalContext'
import { useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import React, { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import LoyaltyBenefitItemTile from 'loyalty/components/LoyaltyBenefitItemTile'
import LoyaltyBenefitCompactRow from 'loyalty/components/LoyaltyBenefitCompactRow'
import LoyaltyEsimDetailsModal from 'loyalty/components/LoyaltyEsimDetailsModal'
import {
  getOrderDisplayDepartureDate,
  getOrderDisplayEndDate,
  isOrderPastTripEndForEsimDisplay,
} from 'loyalty/lib/getEligibleEsimOrders'
import Group from 'components/utils/Group'
import { isWithinEsimRedemptionWindow } from 'loyalty/lib/loyaltyEsimUtils'
import { useLoyaltyEsimImage } from 'loyalty/constants/loyaltyEsimConstants'
import useEsimBenefitTileMessages from 'loyalty/hooks/useEsimBenefitTileMessages'

interface Props {
  order: App.Order
  layout?: 'tile' | 'row'
}

function LoyaltyOrderViewBenefitsEsimItem({ order, layout = 'tile' }: Props) {
  const intl = useIntl()
  const currentTier = useAppSelector(getLoyaltyDisplayTier)
  const tier = order.loyaltyDetails?.tier ?? currentTier
  const loyaltyConfig = useAppSelector(state => state.loyalty.config)
  const gbValue = useMemo(() => {
    if (!loyaltyConfig || !tier) return undefined
    const esimBenefit = loyaltyConfig.regionTierBenefits[tier]?.find(b => b.code === 'esim_package')
    return esimBenefit?.value?.unit === 'GB' ? esimBenefit.value.amount : undefined
  }, [loyaltyConfig, tier])
  const showModal = useContext(ModalContext)
  const esimImage = useLoyaltyEsimImage()

  const departureDate = getOrderDisplayDepartureDate(order)
  const endDate = getOrderDisplayEndDate(order)
  const isWithinSevenDays = useMemo(() => isWithinEsimRedemptionWindow(departureDate, endDate), [departureDate, endDate])

  const { heading, description, addedLabel, detailsLabel } = useEsimBenefitTileMessages(gbValue ?? 0)

  const availabilityNote = intl.formatMessage({
    id: '6ys12L230LSnWXi4Jmn0',
    defaultMessage: 'Available after booking. Activates 7 days before departure',
    description: 'eSIM availability note showing activation timeline',
  })

  const redeemLabel = intl.formatMessage({
    id: 'VHz2lQb8PWn8-_kQU5_-',
    defaultMessage: 'Redeem',
    description: 'Button to open eSIM details/redeem modal on order and booking confirmation',
  })

  const onActionClick = useCallback(() => {
    showModal(<LoyaltyEsimDetailsModal orderId={order.id} />)
  }, [showModal, order.id])

  if (!gbValue) return null

  if (isOrderPastTripEndForEsimDisplay(order)) return null

  const actionButton = (
    <TextButton kind="secondary" onClick={onActionClick}>
      {isWithinSevenDays ? redeemLabel : detailsLabel}
    </TextButton>
  )

  if (layout === 'row') {
    const rowHeading = intl.formatMessage(
      { id: '7mK-OaPGeHWvL6w2E7q-', defaultMessage: '{gb} GB International Data', description: 'eSIM data benefit label on the upcoming escapes card' },
      { gb: gbValue },
    )
    const activationNote = intl.formatMessage({
      id: 'z3xS9H2PqLS5EP2t21gK',
      defaultMessage: 'Activate 7 days before departure',
      description: 'eSIM activation timing note on the upcoming escapes card',
    })
    const viewLabel = intl.formatMessage({
      id: '2PotyrTgljknniT_VfLn',
      defaultMessage: 'View',
      description: 'Button to view eSIM benefit details on the upcoming escapes card',
    })
    return (
      <LoyaltyBenefitCompactRow
        benefitType="esim_package"
        heading={rowHeading}
        status={!isWithinSevenDays && (
          <BodyText variant="medium" colour="highlight-primary-dark" weight="bold">
            {activationNote}
          </BodyText>
        )}
        action={
          <TextButton kind="secondary" variant="dark" size="small" shape="round" onClick={onActionClick}>
            {isWithinSevenDays ? redeemLabel : viewLabel}
          </TextButton>
        }
      />
    )
  }

  return (
    <LoyaltyBenefitItemTile
      heading={heading}
      labels={
        <StatusLabel variant="confirmation" startIcon={<LineCheckCircleIcon />}>
          {addedLabel}
        </StatusLabel>
      }
      image={esimImage}
      actions={actionButton}
    >
      <Group direction="vertical" gap={12}>
        <BodyText variant="medium">{description}</BodyText>
        {!isWithinSevenDays && (
          <BodyText variant="medium" colour="highlight-primary-dark" weight="bold">
            {availabilityNote}
          </BodyText>
        )}
      </Group>
    </LoyaltyBenefitItemTile>
  )
}

export default LoyaltyOrderViewBenefitsEsimItem
